// src/components/ChangePrimaryPhone.tsx
// ============================================================================
// FILE ROLE: Modal flow for changing the user's primary phone number.
// Mirrors ChangePrimaryEmail.tsx but uses the server-side phone OTP path:
//   - requestPhoneOtp({ phone, forceReset: true }) → sends SMS via SimpleText
//   - verifyContactPointOTP({ phone, otpCode }) → flips User.phone and
//     phoneVerified, creates ContactPoint row for the new number
//
// On success, the parent's onSuccess callback receives both newPhone and
// oldPhone so it can demote the old phone to a regular vault item via
// updateContactPointsVault.
// ============================================================================

import { useState } from "react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "../../amplify/data/resource";

const client = generateClient<Schema>();

interface ChangePrimaryPhoneProps {
  currentPhone: string;
  onSuccess: (newPhoneE164: string, oldPhone: string) => Promise<void>;
  onCancel: () => void;
}

function digitsOnly(s: string) {
  return s.replace(/\D/g, "");
}

function formatUSPhone(d: string) {
  const n = digitsOnly(d).slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}

function toE164(n10: string) {
  const n = digitsOnly(n10);
  return n.length === 10 ? `+1${n}` : "";
}

const ChangePrimaryPhone = ({ currentPhone, onSuccess, onCancel }: ChangePrimaryPhoneProps) => {
  const [phoneDigits, setPhoneDigits] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState<"input" | "verify">("input");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  const isValidUS = phoneDigits.length === 10;

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!isValidUS) {
      setError("Please enter a valid 10-digit US phone number.");
      return;
    }

    if (!smsConsent) {
      setError("Please agree to receive a verification code via SMS.");
      return;
    }

    const newE164 = toE164(phoneDigits);
    if (!newE164) {
      setError("Failed to normalize phone number.");
      return;
    }

    if (newE164 === currentPhone) {
      setError("New phone must be different from current phone.");
      return;
    }

    setLoading(true);
    try {
      const response = await client.mutations.requestPhoneOtp({
        phone: newE164,
        forceReset: true,
      });

      if (response.errors && response.errors.length > 0) {
        setError("Failed to send verification code.");
        return;
      }

      let parsed: any = response.data;
      if (typeof parsed === "string") {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
      }

      if (parsed?.ok) {
        setSuccess("Verification code sent!");
        setStep("verify");
      } else {
        const reason = parsed?.reason;
        if (reason === "COOLDOWN_ACTIVE") {
          setError("Too many attempts. Please try again later.");
        } else if (reason === "SMS_SEND_FAILED") {
          setError("We couldn't send the code. Please try again.");
        } else {
          setError("Failed to send verification code.");
        }
      }
    } catch (err) {
      setError("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (verificationCode.length !== 6) {
      setError("Please enter the 6-digit code.");
      return;
    }

    const newE164 = toE164(phoneDigits);
    if (!newE164) {
      setError("Failed to normalize phone number.");
      return;
    }

    setLoading(true);
    try {
      const response = await client.mutations.verifyContactPointOTP({
        phone: newE164,
        otpCode: verificationCode,
      });

      if (response.errors && response.errors.length > 0) {
        setError("Verification failed. Please try again.");
        return;
      }

      let parsed: any = response.data;
      if (typeof parsed === "string") {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
      }

      if (parsed?.ok) {
        setSuccess("Phone updated successfully!");
        // Give user a moment to see success message before parent handles
        // the vault demote + state refresh + close.
        setTimeout(() => {
          onSuccess(newE164, currentPhone);
        }, 1500);
      } else {
        const reason = parsed?.reason;
        if (reason === "INVALID_CODE") {
          setError("Incorrect code. Please try again.");
        } else if (reason === "OTP_EXPIRED") {
          setStep("input");
          setVerificationCode("");
          setError("Your code expired. Please request a new one.");
        } else if (reason === "PHONE_CLAIMED_BY_OTHER") {
          setError("This phone number is already associated with another account.");
        } else {
          setError("Verification failed. Please try again.");
        }
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl border-4 border-gray-950 max-w-md w-full p-6">
        <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>
          Change Primary Phone
        </h2>

        <div className="mb-4 p-3 bg-gray-100 rounded">
          <p className="text-sm text-gray-600">Current Phone:</p>
          <p className="font-medium text-gray-800">{currentPhone}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {step === "input" ? (
          <form onSubmit={handleSendCode}>
            <div className="mb-4">
              <label
                htmlFor="newPhone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                New Phone Number
              </label>
              <input
                id="newPhone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={formatUSPhone(phoneDigits)}
                onChange={(e) => {
                  setPhoneDigits(digitsOnly(e.target.value).slice(0, 10));
                  setError("");
                }}
                placeholder="(555) 555-5555"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
              <p className="text-xs" style={{ color: '#555' }}>
                <strong>Note:</strong> A verification code will be sent to your new number.
                Your current number will remain accessible as a contact point that can be deleted.
              </p>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingBottom: 8 }}>
              <input
                id="sms-consent-change"
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer accent-[#E05A3A]"
                disabled={loading}
              />
              <label
                htmlFor="sms-consent-change"
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: "#555",
                  lineHeight: 1.5,
                }}
              >
                I agree to receive a one-time verification code via text (SMS) from CRUSHHH to verify my phone number. Message and data rates may apply. Up to (1) message per verification attempt. Reply STOP to cancel, HELP for help.
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !isValidUS || !smsConsent}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white ${
                  loading || !isValidUS || !smsConsent
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-brand hover:bg-branddark"
                }`}
              >
                {loading ? "Sending..." : "Send Code"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Enter the verification code sent to:
              </p>
              <p className="font-medium text-gray-800 mb-4">{formatUSPhone(phoneDigits)}</p>

              <input
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError("");
                }}
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-center tracking-widest"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setStep("input");
                  setVerificationCode("");
                  setError("");
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white ${
                  loading || verificationCode.length !== 6
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-brand hover:bg-branddark"
                }`}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </div>

            <p className="text-xs text-center text-gray-500 mt-3">
              Didn't receive the code? Check your messages or{" "}
              <button
                type="button"
                onClick={() => {
                  setStep("input");
                  setVerificationCode("");
                  setError("");
                }}
                style={{ color: '#c0645a' }} className="underline"
              >
                try again
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePrimaryPhone;
